import React, { useContext, useState } from 'react';
import {
    Alert,
    AlertDescription,
    AlertIcon,
    Badge,
    Box,
    Button,
    Checkbox,
    Container,
    FormControl,
    FormHelperText,
    FormLabel,
    Grid,
    Heading,
    HStack,
    Input,
    SimpleGrid,
    Text,
    Textarea,
    VStack,
    useToast,
} from '@chakra-ui/react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import Footer from '../compnents/Footer';
import Loading from '../compnents/Loading';
import Navbar from '../compnents/Navbar';
import SideBar from '../compnents/SideBar';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

const REPORT_METRICS = [
    ['invitations_sent', 'Invitations sent'],
    ['completed', 'Completed pulses'],
    ['completion_rate', 'Completion rate'],
    ['revoked', 'Consent revoked'],
    ['consented_share_with_firm', 'Shared with firm'],
    ['consented_aggregate_only', 'Aggregate only'],
    ['active_sponsored_entitlements', 'Active invitations'],
    ['advisor_actions', 'Adviser actions'],
    ['advisor_outcomes', 'Recorded outcomes'],
];

const formatMetric = (key, value) => {
    if (key === 'completion_rate') return `${Math.round(Number(value || 0) * 100)}%`;
    return value ?? '—';
};

const ConnectedPilot = () => {
    const { token, user, isAuthInitializing } = useContext(AuthContext);
    const toast = useToast();
    const [campaignName, setCampaignName] = useState('');
    const [shareWithFirm, setShareWithFirm] = useState(true);
    const [aggregateOnly, setAggregateOnly] = useState(true);
    const [campaign, setCampaign] = useState(null);
    const [clientRef, setClientRef] = useState('');
    const [rmId, setRmId] = useState('');
    const [invitation, setInvitation] = useState(null);
    const [report, setReport] = useState(null);
    const [busy, setBusy] = useState(false);

    if (isAuthInitializing || !token || !user) return <Loading />;

    const authConfig = { headers: { Authorization: `Bearer ${token}` } };
    const showError = (title, error) => toast({
        title,
        description: error.response?.data?.error?.detail || error.response?.data?.message || error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
    });

    const createCampaign = async event => {
        event.preventDefault();
        const scopeOffered = [
            ...(shareWithFirm ? ['share_with_firm'] : []),
            ...(aggregateOnly ? ['aggregate_only'] : []),
        ];
        if (scopeOffered.length === 0) {
            toast({ title: 'Select at least one consent scope', status: 'warning' });
            return;
        }
        setBusy(true);
        try {
            const response = await axios.post(`${API_BASE_URL}/content/connected/campaigns`, {
                name: campaignName,
                scope_offered: scopeOffered,
                token_ttl_days: 7,
                retention_days: 365,
            }, authConfig);
            setCampaign(response.data);
            setInvitation(null);
            setReport(null);
        } catch (error) {
            showError('Campaign could not be created', error);
        } finally {
            setBusy(false);
        }
    };

    const createInvitation = async event => {
        event.preventDefault();
        setBusy(true);
        try {
            const response = await axios.post(
                `${API_BASE_URL}/content/connected/campaigns/${campaign.campaign_id}/invitations`,
                { client_ref: clientRef, ...(rmId ? { rm_id: rmId } : {}) },
                authConfig,
            );
            setInvitation(response.data);
            setClientRef('');
        } catch (error) {
            showError('Invitation could not be generated', error);
        } finally {
            setBusy(false);
        }
    };

    const loadReport = async () => {
        setBusy(true);
        try {
            const response = await axios.get(
                `${API_BASE_URL}/content/connected/campaigns/${campaign.campaign_id}/report`,
                authConfig,
            );
            setReport(response.data);
        } catch (error) {
            showError('Campaign report could not be loaded', error);
        } finally {
            setBusy(false);
        }
    };

    const copyToken = async () => {
        try {
            await navigator.clipboard.writeText(invitation.token);
            toast({ title: 'Invitation token copied', status: 'success', duration: 2500 });
        } catch (error) {
            showError('Token could not be copied', error);
        }
    };

    return (
        <Box minH="100vh" display="flex" flexDirection="column" bg="brand.bg">
            <Navbar />
            <Box display="flex" flex="1">
                <SideBar />
                <Container maxW="container.xl" flex="1" py={8} px={{ base: 4, md: 6 }}>
                    <VStack align="stretch" spacing={6}>
                        <Box>
                            <HStack spacing={3} mb={2}>
                                <Heading size="lg">Connected pilot</Heading>
                                <Badge colorScheme="orange">Not generally available</Badge>
                            </HStack>
                            <Text color="gray.600">
                                Create a sponsored pulse campaign, generate pseudonymous invitations,
                                and monitor campaign-level participation.
                            </Text>
                        </Box>

                        <Alert status="info" borderRadius="lg">
                            <AlertIcon />
                            <AlertDescription>
                                Aggregate-only consent is reported as counts. Individual response content is never shown here.
                            </AlertDescription>
                        </Alert>

                        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                            <Box as="form" onSubmit={createCampaign} bg="white" borderWidth="1px" borderRadius="xl" p={6}>
                                <VStack align="stretch" spacing={4}>
                                    <Heading size="md">1. Create campaign</Heading>
                                    <FormControl isRequired>
                                        <FormLabel>Campaign name</FormLabel>
                                        <Input value={campaignName} onChange={event => setCampaignName(event.target.value)} maxLength={80} />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>Consent scopes offered</FormLabel>
                                        <VStack align="start">
                                            <Checkbox isChecked={shareWithFirm} onChange={event => setShareWithFirm(event.target.checked)}>
                                                Share with firm
                                            </Checkbox>
                                            <Checkbox isChecked={aggregateOnly} onChange={event => setAggregateOnly(event.target.checked)}>
                                                Aggregate only
                                            </Checkbox>
                                        </VStack>
                                        <FormHelperText>The client chooses one offered scope in the sponsored pulse.</FormHelperText>
                                    </FormControl>
                                    <Button type="submit" colorScheme="teal" isLoading={busy}>Create campaign</Button>
                                </VStack>
                            </Box>

                            <Box as="form" onSubmit={createInvitation} bg="white" borderWidth="1px" borderRadius="xl" p={6} opacity={campaign ? 1 : 0.55}>
                                <VStack align="stretch" spacing={4}>
                                    <Heading size="md">2. Generate invitation</Heading>
                                    <FormControl isRequired isDisabled={!campaign}>
                                        <FormLabel>Opaque client reference</FormLabel>
                                        <Input value={clientRef} onChange={event => setClientRef(event.target.value)} placeholder="CRM-00042" />
                                        <FormHelperText>No email, URL, name, or other personal data.</FormHelperText>
                                    </FormControl>
                                    <FormControl isDisabled={!campaign}>
                                        <FormLabel>Relationship Manager ID (optional)</FormLabel>
                                        <Input value={rmId} onChange={event => setRmId(event.target.value)} />
                                    </FormControl>
                                    <Button type="submit" colorScheme="teal" isDisabled={!campaign} isLoading={busy}>Generate invitation</Button>
                                </VStack>
                            </Box>
                        </SimpleGrid>

                        {campaign ? (
                            <Box bg="white" borderWidth="1px" borderRadius="xl" p={6}>
                                <HStack justify="space-between" align="start" flexWrap="wrap" gap={3}>
                                    <Box>
                                        <Heading size="md">{campaign.name}</Heading>
                                        <Text color="gray.500" fontSize="sm">Taxonomy {campaign.taxonomy_version} · Campaign {campaign.campaign_id}</Text>
                                    </Box>
                                    <Button onClick={loadReport} isLoading={busy} variant="outline">Refresh aggregate report</Button>
                                </HStack>
                                {report ? (
                                    <Grid templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }} gap={4} mt={5}>
                                        {REPORT_METRICS.map(([key, label]) => (
                                            <Box key={key} borderWidth="1px" borderRadius="lg" p={4}>
                                                <Text fontSize="sm" color="gray.500">{label}</Text>
                                                <Text fontSize="2xl" fontWeight="800">{formatMetric(key, report[key])}</Text>
                                            </Box>
                                        ))}
                                    </Grid>
                                ) : null}
                            </Box>
                        ) : null}

                        {invitation ? (
                            <Box bg="white" borderWidth="1px" borderRadius="xl" p={6}>
                                <Heading size="md" mb={2}>Invitation generated</Heading>
                                <Text fontSize="sm" color="gray.600" mb={3}>
                                    Expires {new Date(invitation.expires_at).toLocaleString()}. Deliver this signed token through the approved pilot channel.
                                </Text>
                                <Textarea value={invitation.token} isReadOnly fontFamily="mono" fontSize="xs" rows={4} aria-label="Signed invitation token" />
                                <Button mt={3} onClick={copyToken}>Copy token</Button>
                            </Box>
                        ) : null}
                    </VStack>
                </Container>
            </Box>
            <Footer />
        </Box>
    );
};

export default ConnectedPilot;
